import {
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Film,
  LogOut,
  AlertCircle,
  Loader2,
  Subtitles,
  Upload,
  Activity,
  Play,
  Keyboard,
  PanelRightClose,
  PanelRightOpen,
  Users,
  MessageSquare,
  RotateCcw,
  Search,
} from 'lucide-react';
import { YouTubeIcon } from '@/components/icons/YouTubeIcon';
import { nanoid } from 'nanoid';
import { VideoPlayer } from '@/components/video/VideoPlayer';
import { YouTubePlayer } from '@/components/video/YouTubePlayer';
import { SubtitleSearchModal } from '@/components/subtitles/SubtitleSearchModal';
import { ChangeMediaModal } from '@/components/room/ChangeMediaModal';
import { ParticipantList } from '@/components/room/ParticipantList';
import { InviteLink } from '@/components/room/InviteLink';
import { ConnectionStatus } from '@/components/room/ConnectionStatus';
import { StreamTelemetryModal } from '@/components/room/StreamTelemetryModal';
import { RoomChat } from '@/components/room/RoomChat';
import { CallControls } from '@/components/call/CallControls';
import { FloatingVideoGrid } from '@/components/call/FloatingVideoGrid';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { useRoomStore } from '@/store/roomStore';
import { usePlayerStore } from '@/store/playerStore';
import { useConnectionStore } from '@/store/connectionStore';
import { useAuth } from '@/hooks/useAuth';
import {
  logRoomJoin,
  logRoomLeave,
  startWatchRecord,
  endWatchRecord,
} from '@/services/activityLogger';
import { useSignaling } from '@/hooks/useSignaling';
import { useWebRTC } from '@/hooks/useWebRTC';
import { useCallMesh } from '@/hooks/useCallMesh';
import { useSync } from '@/hooks/useSync';
import { useVideoCapture } from '@/hooks/useVideoCapture';
import { useSubtitles } from '@/hooks/useSubtitles';
import { hostVideoSender, guestVideoReceiver } from '@/services/videoTransferService';
import type {
  SignalingMessage,
  JoinPayload,
  JoinedPayload,
  SyncPayload,
  OfferPayload,
  AnswerPayload,
  IceCandidatePayload,
  Participant,
  SubtitleChunkPayload,
  MovieMetaPayload,
  ChatMessage,
  FloatingReaction,
  SpeedSyncPayload,
  TransferHostPayload,
  VideoQuality,
} from '@/types';

export function WatchRoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();

  const {
    room,
    participantId,
    participantName,
    role,
    setRoom,
    setParticipant,
    addParticipant,
    removeParticipant,
    transferHost,
    clearRoom,
  } = useRoomStore();

  const { user } = useAuth();
  const roomActivityIdRef = useRef<string | null>(null);
  const watchRecordIdRef = useRef<string | null>(null);

  const {
    duration,
    setDuration,
    setCurrentTime,
    setPlaying,
    subtitleCues,
    subtitleOffset,
    setSubtitleOffset,
    addChatMessage,
    addReaction,
    unreadChatCount,
    clearUnreadChat,
    incrementUnreadChat,
    setPlaybackRate,
    lockSync,
    guestStreamSrc,
  } = usePlayerStore();

  // ── Refs ────────────────────────────────────────────────────
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const subtitleInputRef = useRef<HTMLInputElement>(null);

  // ── UI States ────────────────────────────────────────────────
  const [hostError, setHostError] = useState<string | null>(null);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [guestWaiting, setGuestWaiting] = useState(true);
  const [isTelemetryOpen, setIsTelemetryOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => (typeof window !== 'undefined' ? window.innerWidth >= 768 : true));
  const [sidebarTab, setSidebarTab] = useState<'room' | 'chat'>('room');
  const [isSubtitleModalOpen, setIsSubtitleModalOpen] = useState(false);
  const [isChangeMediaOpen, setIsChangeMediaOpen] = useState(false);

  // ── Activity Logging for Authenticated Host ─────────────────
  useEffect(() => {
    // Only log activity for authenticated room hosts
    if (role !== 'host' || !user || !roomId) return;

    let mounted = true;
    (async () => {
      try {
        const activityId = await logRoomJoin(user.uid, roomId, 'host');
        if (mounted && activityId) {
          roomActivityIdRef.current = activityId;
        }
      } catch (err) {
        console.warn('[ActivityLogger] Error logging room join:', err);
      }
    })();

    return () => {
      mounted = false;
      if (roomActivityIdRef.current && user) {
        logRoomLeave(user.uid, roomActivityIdRef.current);
      }
      if (watchRecordIdRef.current && user) {
        endWatchRecord(user.uid, watchRecordIdRef.current, videoRef.current?.currentTime);
      }
    };
  }, [role, user, roomId]);

  // ── Signaling & Transport ───────────────────────────────────
  const { send, on, disconnect } = useSignaling(roomId);
  const {
    setLocalStream,
    initiateOffer,
    handleOffer,
    handleAnswer,
    handleIceCandidate,
    startGuestStats,
    setPeerQuality,
    setAllPeersQuality,
    closePeer,
    closeAll,
  } = useWebRTC({ participantId, role, send });

  // ── Optional On-Demand Voice & Video Mesh ───────────────────
  const {
    localMicEnabled,
    localCamEnabled,
    isConnectingMedia,
    toggleMicrophone,
    toggleCamera,
  } = useCallMesh({
    participantId,
    send,
    on,
  });

  // ── Host Video Capture ──────────────────────────────────────
  const { startCapture, error: captureError } = useVideoCapture();

  // ── Playback Synchronization ────────────────────────────────
  const { onLocalPlay, onLocalPause, onLocalSeek, applyRemoteSync } = useSync({
    videoRef,
    send,
    participantId,
    role,
  });

  // ── Subtitles ────────────────────────────────────────────────
  const { loadFile: loadSubtitleFile, loadFromVTT, updateSubtitle } = useSubtitles();

  // ── Navigation Guard ─────────────────────────────────────────
  useEffect(() => {
    if (!room || !participantId) {
      navigate(roomId ? `/join/${roomId}` : '/');
    }
  }, [room, participantId, roomId, navigate]);

  // ── HOST: Mount local movie file ─────────────────────────────
  useEffect(() => {
    if (role !== 'host') return;
    if (room?.mediaType === 'youtube') {
      setIsStreamReady(true);
      setHostError(null);
      return;
    }

    const file = (window as Window & { __cinesync_file?: File }).__cinesync_file;
    if (!file) {
      setHostError('No video file selected. Please choose a local file or switch to a YouTube stream.');
      return;
    }

    const url = URL.createObjectURL(file);
    blobUrlRef.current = url;
    setHostError(null);

    // Initialize the YouTube-style advance RAM buffer sender
    hostVideoSender.setMedia(file, room?.movieDuration || 0, roomId || '');

    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [role, room?.mediaType, room?.movieDuration, roomId]);

  // ── HOST: Start media stream capture ─────────────────────────
  const handleHostStartBroadcast = useCallback(async () => {
    if (role !== 'host') return;
    const video = videoRef.current;
    if (!video) return;

    const stream = await startCapture(video);
    if (!stream) return;

    setLocalStream(stream);
    setIsStreamReady(true);

    if (user && room && !watchRecordIdRef.current) {
      startWatchRecord(user.uid, {
        title: room.movieTitle,
        mediaType: 'local',
        roomId,
        totalDuration: video.duration || 0,
      }).then((watchId) => {
        if (watchId) watchRecordIdRef.current = watchId;
      });
    }

    if (room) {
      send({
        type: 'MOVIE_META',
        from: participantId ?? undefined,
        payload: {
          title: room.movieTitle,
          duration: video.duration,
          size: (window as Window & { __cinesync_file?: File }).__cinesync_file?.size ?? 0,
          type: (window as Window & { __cinesync_file?: File }).__cinesync_file?.type ?? '',
        } satisfies MovieMetaPayload,
      });
    }
  }, [role, startCapture, setLocalStream, room, send, participantId, user, roomId]);

  // ── HOST: Auto-initialize broadcast stream when media is loaded
  useEffect(() => {
    if (role !== 'host' || isStreamReady || room?.mediaType === 'youtube') return;
    const video = videoRef.current;
    if (!video) return;

    const autoStart = () => {
      if (!isStreamReady) {
        handleHostStartBroadcast().catch(() => {});
      }
    };

    if (video.readyState >= 1) {
      autoStart();
    } else {
      video.addEventListener('loadedmetadata', autoStart, { once: true });
      video.addEventListener('canplay', autoStart, { once: true });
    }
  }, [role, isStreamReady, room?.mediaType, handleHostStartBroadcast]);

  // ── GUEST: Handle incoming remote stream ─────────────────────
  useEffect(() => {
    if (role !== 'guest') return;
    if (room?.mediaType === 'youtube') {
      setGuestWaiting(false);
      return;
    }

    const handleRemoteStream = (e: Event) => {
      const detail = (e as CustomEvent<{ peerId: string; stream: MediaStream }>).detail;
      remoteStreamRef.current = detail.stream;
      setRemoteStream(detail.stream);
      setGuestWaiting(false);
      startGuestStats(detail.peerId);
    };

    const handleStreamReady = () => {
      setGuestWaiting(false);
    };

    window.addEventListener('cinesync:remotestream', handleRemoteStream);
    window.addEventListener('cinesync:stream-meta', handleStreamReady);
    window.addEventListener('cinesync:stream-ready', handleStreamReady);
    return () => {
      window.removeEventListener('cinesync:remotestream', handleRemoteStream);
      window.removeEventListener('cinesync:stream-meta', handleStreamReady);
      window.removeEventListener('cinesync:stream-ready', handleStreamReady);
    };
  }, [role, startGuestStats, room?.mediaType]);

  // ── Signaling: Join Room Handshake ──────────────────────────
  useEffect(() => {
    if (!participantId || !participantName || !role || !roomId) return;

    const hostUid = role === 'host' ? (room?.hostUid || user?.uid) : undefined;
    const hostEmail = role === 'host' ? user?.email : undefined;

    // Send JOIN immediately (signaling service queues it if connecting)
    send({
      type: 'JOIN',
      from: participantId,
      payload: {
        roomId,
        participantId,
        name: participantName,
        role,
        hostUid,
        hostEmail,
        mediaType: room?.mediaType,
        youtubeVideoId: room?.youtubeVideoId,
        movieTitle: room?.movieTitle,
      } as any,
    });
  }, [participantId, participantName, role, roomId, room?.hostUid, room?.mediaType, room?.youtubeVideoId, room?.movieTitle, user?.uid, user?.email, send]);

  // ── Signaling: Message Ingestion ────────────────────────────
  useEffect(() => {
    const offJoined = on('JOINED', (msg: SignalingMessage) => {
      const payload = msg.payload as JoinedPayload;
      const currentRoom = useRoomStore.getState().room;
      const mediaType = payload.room.mediaType || currentRoom?.mediaType || 'local';
      const youtubeVideoId = payload.room.youtubeVideoId || currentRoom?.youtubeVideoId;
      const mergedRoom = {
        ...payload.room,
        mediaType,
        youtubeVideoId,
        movieTitle: payload.room.movieTitle || currentRoom?.movieTitle || 'Screening Room',
      };
      setRoom(mergedRoom);

      if (mediaType === 'youtube') {
        setIsStreamReady(true);
        setGuestWaiting(false);
        setHostError(null);
      }

      if (payload.room.movieDuration) {
        setDuration(payload.room.movieDuration);
      }
      if (payload.room.currentTime !== undefined) {
        setCurrentTime(payload.room.currentTime);
      }
      if (payload.room.playbackState) {
        setPlaying(payload.room.playbackState === 'playing');
      }
      const myParticipant = payload.room.participants.find((p) => p.id === payload.yourId);
      if (myParticipant && myParticipant.role !== role) {
        setParticipant(payload.yourId, myParticipant.name, myParticipant.role);
      }
      if (payload.room.chatHistory && payload.room.chatHistory.length > 0) {
        payload.room.chatHistory.forEach((m) => addChatMessage(m));
      }
    });

    const offParticipantJoined = on('PARTICIPANT_JOINED', (msg: SignalingMessage) => {
      const p = msg.payload as Participant;
      addParticipant(p);

      if (role === 'host' && p.id !== participantId) {
        initiateOffer(p.id);

        // Push current movie metadata & duration to newly joined guest immediately
        const video = videoRef.current;
        const curDur = video && !video.srcObject && video.duration > 0 ? video.duration : (room?.movieDuration || 0);
        if (curDur > 0 && room) {
          send({
            type: 'MOVIE_META',
            to: p.id,
            from: participantId ?? undefined,
            payload: {
              title: room.movieTitle,
              duration: curDur,
              size: (window as Window & { __cinesync_file?: File }).__cinesync_file?.size ?? 0,
              type: (window as Window & { __cinesync_file?: File }).__cinesync_file?.type ?? '',
            } satisfies MovieMetaPayload,
          });
        }
      }
    });

    const offQualityChange = on('QUALITY_CHANGE', (msg: SignalingMessage) => {
      if (role === 'host' && msg.from) {
        const payload = msg.payload as { resolution: VideoQuality };
        if (payload?.resolution) {
          setPeerQuality(msg.from, payload.resolution);
        }
      }
    });

    const isForMe = (msg: SignalingMessage) => {
      if (!msg.to) return true;
      return msg.to === participantId;
    };

    const offParticipantLeft = on('PARTICIPANT_LEFT', (msg: SignalingMessage) => {
      const { participantId: leftId } = msg.payload as { participantId: string };
      removeParticipant(leftId);
      closePeer(leftId);
    });

    const offOffer = on('OFFER', async (msg: SignalingMessage) => {
      if (role !== 'guest' || !isForMe(msg)) return;
      const payload = msg.payload as OfferPayload;
      await handleOffer(msg.from!, payload.sdp);
    });

    const offAnswer = on('ANSWER', async (msg: SignalingMessage) => {
      if (role !== 'host' || !isForMe(msg)) return;
      const payload = msg.payload as AnswerPayload;
      await handleAnswer(msg.from!, payload.sdp);
    });

    const offIce = on('ICE_CANDIDATE', async (msg: SignalingMessage) => {
      if (!isForMe(msg)) return;
      const payload = msg.payload as IceCandidatePayload;
      await handleIceCandidate(msg.from!, payload.candidate);
    });

    const offSync = on('SYNC', (msg: SignalingMessage) => {
      if (msg.from === participantId) return;
      const payload = msg.payload as SyncPayload;
      applyRemoteSync(payload);
    });

    const offRoomState = on('ROOM_STATE', (msg: SignalingMessage) => {
      const newRoom = msg.payload as typeof room;
      if (newRoom) {
        setRoom(newRoom);
        if (newRoom.movieDuration) setDuration(newRoom.movieDuration);
        if (newRoom.currentTime !== undefined) setCurrentTime(newRoom.currentTime);
        if (newRoom.playbackState) setPlaying(newRoom.playbackState === 'playing');
      }
    });

    const offSubtitle = on('SUBTITLE_CHUNK', (msg: SignalingMessage) => {
      if (role !== 'guest') return;
      const payload = msg.payload as SubtitleChunkPayload;
      loadFromVTT(payload.vttContent);
    });

    const offChat = on('CHAT_MESSAGE', (msg: SignalingMessage) => {
      const payload = msg.payload as ChatMessage;
      addChatMessage(payload);
      if (!isSidebarOpen || sidebarTab !== 'chat') {
        incrementUnreadChat();
      }
    });

    const offReaction = on('REACTION', (msg: SignalingMessage) => {
      const payload = msg.payload as FloatingReaction;
      addReaction(payload);
    });

    const offSpeed = on('SPEED_SYNC', (msg: SignalingMessage) => {
      const payload = msg.payload as SpeedSyncPayload;
      if (videoRef.current) {
        videoRef.current.playbackRate = payload.playbackRate;
      }
      setPlaybackRate(payload.playbackRate);
    });

    const offHostTransferred = on('HOST_TRANSFERRED', (msg: SignalingMessage) => {
      const { newHostId } = msg.payload as { newHostId: string; newHostName: string };
      transferHost(newHostId);
    });

    const offResyncRequest = on('RESYNC_REQUEST', (msg: SignalingMessage) => {
      if (role === 'host' && videoRef.current && room) {
        send({
          type: 'RESYNC_RESPONSE',
          from: participantId ?? undefined,
          to: msg.from,
          payload: {
            currentTime: videoRef.current.currentTime,
            playbackState: videoRef.current.paused ? 'paused' : 'playing',
            playbackRate: videoRef.current.playbackRate,
            version: room.version + 1,
            targetId: msg.from,
          },
        });
      }
    });

    const offResyncResponse = on('RESYNC_RESPONSE', (msg: SignalingMessage) => {
      const payload = msg.payload as {
        currentTime: number;
        playbackState: 'playing' | 'paused';
        playbackRate: number;
        version: number;
      };
      if (videoRef.current) {
        lockSync(800);
        if (!videoRef.current.srcObject) {
          videoRef.current.currentTime = payload.currentTime;
        }
        setCurrentTime(payload.currentTime);
        setPlaying(payload.playbackState === 'playing');
        videoRef.current.playbackRate = payload.playbackRate || 1;
        setPlaybackRate(payload.playbackRate || 1);
        if (payload.playbackState === 'playing') {
          videoRef.current.play().catch(() => { });
        } else {
          videoRef.current.pause();
        }
      }
    });

    const offMovieMeta = on('MOVIE_META', (msg: SignalingMessage) => {
      const payload = msg.payload as MovieMetaPayload;
      const currentRoom = useRoomStore.getState().room;
      if (currentRoom) {
        setRoom({ ...currentRoom, movieTitle: payload.title, movieDuration: payload.duration });
      }
      setDuration(payload.duration);
    });

    const offHostDisconnected = on('HOST_DISCONNECTED', () => {
      if (role === 'guest') {
        setHostError('The host disconnected. The movie stream has ended.');
      }
    });

    const offMediaChange = on('MEDIA_CHANGE', (msg: SignalingMessage) => {
      const payload = msg.payload as any;
      if (!payload) return;
      const currentRoom = useRoomStore.getState().room;
      if (!currentRoom) return;
      setRoom({
        ...currentRoom,
        mediaType: payload.mediaType || 'local',
        youtubeVideoId: payload.youtubeVideoId,
        movieTitle: payload.title || currentRoom.movieTitle,
        movieDuration: payload.duration || 0,
        playbackState: 'paused',
        currentTime: 0,
        version: payload.version || currentRoom.version + 1,
      });
      setPlaybackRate(1);
      if (payload.mediaType === 'youtube') {
        setIsStreamReady(true);
        setGuestWaiting(false);
      }
    });

    const offRoomClosed = on('ROOM_CLOSED', () => {
      setHostError('The watch room has been closed.');
      setTimeout(() => navigate('/'), 3000);
    });

    return () => {
      offJoined();
      offParticipantJoined();
      offParticipantLeft();
      offOffer();
      offAnswer();
      offIce();
      offSync();
      offRoomState();
      offSubtitle();
      offChat();
      offReaction();
      offSpeed();
      offHostTransferred();
      offResyncRequest();
      offResyncResponse();
      offMovieMeta();
      offHostDisconnected();
      offMediaChange();
      offQualityChange();
      offRoomClosed();
    };
  }, [
    on, role, participantId, setRoom, setParticipant, addParticipant, removeParticipant,
    initiateOffer, handleOffer, handleAnswer, handleIceCandidate, closePeer, setPeerQuality,
    applyRemoteSync, loadFromVTT, navigate, addChatMessage, addReaction,
    incrementUnreadChat, isSidebarOpen, sidebarTab, setPlaybackRate,
    transferHost, send, room, lockSync, setDuration,
  ]);

  // ── Broadcast leave immediately when tab or browser is closed ─
  useEffect(() => {
    const handleLeave = () => {
      if (participantId) {
        try {
          send({
            type: 'PARTICIPANT_LEFT',
            from: participantId,
            payload: { participantId },
          });
        } catch { }
      }
      disconnect();
    };

    window.addEventListener('beforeunload', handleLeave);
    window.addEventListener('pagehide', handleLeave);
    return () => {
      window.removeEventListener('beforeunload', handleLeave);
      window.removeEventListener('pagehide', handleLeave);
    };
  }, [participantId, send, disconnect]);

  // ── Media Source Switching (Host Only) ───────────────────────
  const handleSwitchToYouTube = useCallback(
    (videoId: string, title: string) => {
      if (role !== 'host' || !room) return;
      send({
        type: 'MEDIA_CHANGE',
        from: participantId ?? undefined,
        payload: {
          mediaType: 'youtube',
          youtubeVideoId: videoId,
          title,
          duration: 0,
        },
      });
      setRoom({
        ...room,
        mediaType: 'youtube',
        youtubeVideoId: videoId,
        movieTitle: title,
        movieDuration: 0,
        playbackState: 'paused',
        currentTime: 0,
        version: room.version + 1,
      });
      setIsStreamReady(true);
      setHostError(null);
    },
    [role, room, send, participantId, setRoom]
  );

  const handleSwitchToLocalFile = useCallback(
    (file: File) => {
      if (role !== 'host' || !room) return;
      (window as Window & { __cinesync_file?: File }).__cinesync_file = file;
      const title = file.name.replace(/\.[^/.]+$/, '');
      const url = URL.createObjectURL(file);
      blobUrlRef.current = url;

      send({
        type: 'MEDIA_CHANGE',
        from: participantId ?? undefined,
        payload: {
          mediaType: 'local',
          youtubeVideoId: undefined,
          title,
          duration: 0,
        },
      });
      setRoom({
        ...room,
        mediaType: 'local',
        youtubeVideoId: undefined,
        movieTitle: title,
        playbackState: 'paused',
        currentTime: 0,
        version: room.version + 1,
      });
      setIsStreamReady(false);
      setHostError(null);
    },
    [role, room, send, participantId, setRoom]
  );

  // ── Leave Room ───────────────────────────────────────────────
  const handleLeave = useCallback(() => {
    if (role === 'host' && user) {
      if (roomActivityIdRef.current) {
        logRoomLeave(user.uid, roomActivityIdRef.current);
        roomActivityIdRef.current = null;
      }
      if (watchRecordIdRef.current) {
        endWatchRecord(user.uid, watchRecordIdRef.current, videoRef.current?.currentTime);
        watchRecordIdRef.current = null;
      }
    }

    send({
      type: 'PARTICIPANT_LEFT',
      from: participantId ?? undefined,
      payload: { participantId },
    });
    disconnect();
    closeAll();
    clearRoom();
    navigate('/');
  }, [send, disconnect, closeAll, clearRoom, navigate, participantId, role, user]);

  // ── Subtitle File Loader ─────────────────────────────────────
  const handleSubtitleFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const vttContent = await loadSubtitleFile(file);
        if (role === 'host') {
          send({
            type: 'SUBTITLE_CHUNK',
            from: participantId ?? undefined,
            payload: { vttContent } satisfies SubtitleChunkPayload,
          });
        }
      } catch (err) {
        console.error('Subtitle parse error:', err);
      }
    },
    [loadSubtitleFile, role, participantId, send]
  );

  // ── Chat & Reactions ─────────────────────────────────────────
  const handleSendMessage = useCallback(
    (text: string) => {
      if (!participantId || !participantName) return;
      const msg: ChatMessage = {
        id: nanoid(10),
        senderId: participantId,
        senderName: participantName,
        text,
        timestamp: Date.now(),
      };
      addChatMessage(msg);
      send({
        type: 'CHAT_MESSAGE',
        from: participantId,
        payload: msg,
      });
    },
    [participantId, participantName, addChatMessage, send]
  );

  const handleSendReaction = useCallback(
    (emoji: string) => {
      if (!participantId || !participantName) return;
      const reaction: FloatingReaction = {
        id: nanoid(10),
        emoji,
        senderName: participantName,
        xOffset: Math.floor(Math.random() * 80) + 10,
        createdAt: Date.now(),
      };
      addReaction(reaction);
      send({
        type: 'REACTION',
        from: participantId,
        payload: reaction,
      });
    },
    [participantId, participantName, addReaction, send]
  );

  // ── Speed Change & Resync ───────────────────────────────────
  const handleSpeedChange = useCallback(
    (speed: number) => {
      if (videoRef.current) {
        videoRef.current.playbackRate = speed;
      }
      setPlaybackRate(speed);
      if (room) {
        send({
          type: 'SPEED_SYNC',
          from: participantId ?? undefined,
          payload: {
            playbackRate: speed,
            version: room.version + 1,
            byName: participantName ?? 'Participant',
          } satisfies SpeedSyncPayload,
        });
      }
    },
    [videoRef, setPlaybackRate, room, send, participantId, participantName]
  );

  const handleResync = useCallback(() => {
    if (role === 'host') {
      if (videoRef.current && room) {
        send({
          type: 'SYNC',
          from: participantId ?? undefined,
          payload: {
            playbackState: videoRef.current.paused ? 'paused' : 'playing',
            currentTime: videoRef.current.currentTime,
            playbackRate: videoRef.current.playbackRate,
            version: room.version + 1,
          } satisfies SyncPayload,
        });
      }
    } else {
      send({
        type: 'RESYNC_REQUEST',
        from: participantId ?? undefined,
        payload: { fromId: participantId, fromName: participantName },
      });
    }
  }, [role, videoRef, room, send, participantId, participantName]);

  // ── Host Transfer ────────────────────────────────────────────
  const handleTransferHost = useCallback(
    (newHostId: string) => {
      if (role !== 'host') return;
      send({
        type: 'TRANSFER_HOST',
        from: participantId ?? undefined,
        payload: { newHostId } satisfies TransferHostPayload,
      });
    },
    [role, send, participantId]
  );

  // ── Host: Remove/Kick participant ────────────────────────────
  const handleRemoveParticipant = useCallback(
    (targetId: string) => {
      if (role !== 'host') return;
      removeParticipant(targetId);
      closePeer(targetId);
      send({
        type: 'PARTICIPANT_LEFT',
        from: participantId ?? undefined,
        payload: { participantId: targetId },
      });
    },
    [role, removeParticipant, closePeer, send, participantId]
  );

  // ── Stream Resolution & Quality Control ──────────────────────
  const handleQualityChange = useCallback(
    (quality: VideoQuality) => {
      if (role === 'guest') {
        const hostId = room?.hostId;
        if (hostId) {
          send({
            type: 'QUALITY_CHANGE',
            to: hostId,
            from: participantId ?? undefined,
            payload: { resolution: quality },
          });
        }
      } else if (role === 'host') {
        setAllPeersQuality(quality);
      }
    },
    [role, room?.hostId, send, participantId, setAllPeersQuality]
  );

  if (!room || !participantId) return null;

  const isHost = role === 'host';
  const hostSrc = isHost && blobUrlRef.current ? blobUrlRef.current : undefined;
  const activeVideoSrc = isHost ? hostSrc : (guestStreamSrc || undefined);

  return (
    <div className="min-h-screen h-[100dvh] bg-cinema-950 text-cinema-100 flex flex-col font-sans select-none overflow-hidden">
      {/* Top Header */}
      <header className="hairline-b bg-cinema-900/90 px-4 sm:px-6 h-14 flex items-center justify-between gap-4 z-40 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded bg-cinema-800 border border-cinema-700 flex items-center justify-center text-lamp flex-shrink-0">
            <Film className="w-4 h-4" />
          </div>
          <div className="truncate flex items-center gap-2">
            <span className="text-sm font-bold text-cinema-100 tracking-tight truncate max-w-xs sm:max-w-md">
              {room.movieTitle}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-cinema-400 bg-cinema-800 border border-cinema-700 px-1.5 py-0.5 rounded flex-shrink-0">
              {isHost ? 'Host' : 'Guest'}
            </span>
            {room.mediaType === 'youtube' && (
              <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-red-400 bg-red-500/10 border border-red-500/20 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                <YouTubeIcon className="w-3 h-3" />
                <span>YouTube</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {isHost && (
            <button
              onClick={() => setIsChangeMediaOpen(true)}
              className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded bg-cinema-800 hover:bg-cinema-750 border border-cinema-700 text-xs text-cinema-300 hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp"
              title="Change Movie File or YouTube Video"
            >
              <Film className="w-3.5 h-3.5 text-lamp" />
              <span>Change Video</span>
            </button>
          )}

          <ConnectionStatus />

          <CallControls
            micEnabled={localMicEnabled}
            camEnabled={localCamEnabled}
            isLoading={isConnectingMedia}
            onToggleMic={toggleMicrophone}
            onToggleCam={toggleCamera}
          />

          <button
            onClick={() => setIsTelemetryOpen(true)}
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded bg-cinema-800 hover:bg-cinema-750 border border-cinema-700 text-xs text-cinema-300 hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
            title="Connection & Stream Quality"
            aria-label="View stream quality"
          >
            <Activity className="w-3.5 h-3.5 text-lamp" />
            <span>Quality</span>
          </button>

          <button
            onClick={() => setIsShortcutsOpen(true)}
            className="hidden sm:flex p-1.5 rounded text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
            title="Keyboard Shortcuts"
            aria-label="Keyboard Shortcuts"
          >
            <Keyboard className="w-4 h-4" />
          </button>

          <button
            onClick={() => {
              if (isSidebarOpen && sidebarTab === 'chat') {
                setIsSidebarOpen(false);
              } else {
                setIsSidebarOpen(true);
                setSidebarTab('chat');
                clearUnreadChat();
              }
            }}
            className="relative p-1.5 rounded text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
            title="Room Chat"
            aria-label="Room Chat"
          >
            <MessageSquare className="w-4 h-4" />
            {unreadChatCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-lamp text-cinema-950 text-[10px] font-bold flex items-center justify-center shadow-sm">
                {unreadChatCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-1.5 rounded text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
            title={isSidebarOpen ? 'Hide Room Details' : 'Show Room Details'}
            aria-label={isSidebarOpen ? 'Hide Room Details' : 'Show Room Details'}
          >
            {isSidebarOpen ? (
              <PanelRightClose className="w-4 h-4" />
            ) : (
              <PanelRightOpen className="w-4 h-4" />
            )}
          </button>

          <ThemeToggle />

          <button
            onClick={handleLeave}
            className="flex items-center gap-1.5 px-3 py-1 rounded text-xs text-cinema-300 hover:text-red-400 hover:bg-red-500/10 border border-cinema-700/80 transition-all duration-150 enabled:active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
            title="Leave Room"
            aria-label="Leave Room"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Leave</span>
          </button>
        </div>
      </header>

      {/* Main Layout: Video Stage + Sidebar */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Video Canvas Stage */}
        <div className="flex-1 flex items-center justify-center bg-black relative min-w-0">
          {/* Error Message */}
          {(hostError || captureError) && (
            <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/85 backdrop-blur-sm p-6">
              <div className="bg-cinema-900 border border-red-500/30 rounded-xl p-8 max-w-md w-full text-center shadow-2xl">
                <AlertCircle className="w-10 h-10 text-reel-rose mx-auto mb-4" />
                <h3 className="text-base font-bold text-cinema-100 mb-2">Stream Ended</h3>
                <p className="text-xs text-cinema-300 leading-relaxed mb-6">
                  {hostError || captureError}
                </p>
                <button
                  onClick={() => navigate('/')}
                  className="px-5 py-2.5 rounded-md bg-cinema-800 hover:bg-cinema-750 border border-cinema-700 text-xs font-semibold text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                >
                  Return to Home
                </button>
              </div>
            </div>
          )}

          {/* Guest Waiting Notice */}
          {!isHost && guestWaiting && (
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/90 p-6">
              <div className="text-center space-y-3">
                <Loader2 className="w-10 h-10 text-lamp animate-spin mx-auto" />
                <div className="text-sm font-semibold text-white">Connecting to Host's Stream…</div>
                <p className="text-xs text-cinema-400 max-w-sm">
                  Waiting for the host to start the video. Playback will begin automatically.
                </p>
              </div>
            </div>
          )}

          {/* Player Canvas: YouTube or Local File */}
          {room.mediaType === 'youtube' && room.youtubeVideoId ? (
            <YouTubePlayer
              videoId={room.youtubeVideoId}
              isHost={isHost}
              onPlay={onLocalPlay}
              onPause={onLocalPause}
              onSeek={onLocalSeek}
              onSpeedChange={handleSpeedChange}
              onEnded={() => {
                if (isHost) {
                  send({
                    type: 'SYNC',
                    from: participantId,
                    payload: {
                      playbackState: 'paused',
                      currentTime: duration,
                      version: (room?.version ?? 0) + 1,
                    } satisfies SyncPayload,
                  });
                }
              }}
              onOpenSubtitleSearch={() => setIsSubtitleModalOpen(true)}
            />
          ) : (
            <VideoPlayer
              src={activeVideoSrc}
              remoteStream={!isHost ? remoteStream : undefined}
              videoRef={videoRef}
              onPlay={() => {
                if (isHost && !isStreamReady) {
                  handleHostStartBroadcast().catch(() => {});
                }
                onLocalPlay();
              }}
              onPause={onLocalPause}
              onSeek={(time) => {
                onLocalSeek(time);
                if (isHost) {
                  hostVideoSender.notifySeek(time);
                } else {
                  guestVideoReceiver.requestSeek(time);
                }
              }}
              onTimeUpdate={updateSubtitle}
              onDurationChange={(d) => {
                if (isHost && room && d > 0) {
                  setDuration(d);
                  setRoom({ ...room, movieDuration: d });
                  hostVideoSender.updateDuration(d);
                  send({
                    type: 'MOVIE_META',
                    from: participantId ?? undefined,
                    payload: {
                      title: room.movieTitle,
                      duration: d,
                      size: (window as Window & { __cinesync_file?: File }).__cinesync_file?.size ?? 0,
                      type: (window as Window & { __cinesync_file?: File }).__cinesync_file?.type ?? '',
                    } satisfies MovieMetaPayload,
                  });
                }
              }}
              onEnded={() => {
                if (isHost) {
                  send({
                    type: 'SYNC',
                    from: participantId,
                    payload: {
                      playbackState: 'paused',
                      currentTime: duration,
                      duration: duration,
                      version: (room?.version ?? 0) + 1,
                    } satisfies SyncPayload,
                  });
                }
              }}
              onSpeedChange={handleSpeedChange}
              onQualityChange={handleQualityChange}
              onResync={handleResync}
              onToggleTelemetry={() => setIsTelemetryOpen(true)}
              onOpenSubtitleSearch={() => setIsSubtitleModalOpen(true)}
            />
          )}

          {/* Optional On-Demand Floating Video Grid & Remote Voice Channels */}
          <FloatingVideoGrid />

          {/* HOST: Start Stream Action Banner (Local Video Only) */}
          {isHost && !isStreamReady && !captureError && room.mediaType !== 'youtube' && (
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/75 backdrop-blur-sm p-6">
              <div className="text-center space-y-4 max-w-md bg-cinema-900/95 border border-cinema-700 p-8 rounded-xl shadow-2xl">
                <div className="w-12 h-12 rounded-full bg-lamp-dim border border-lamp/30 flex items-center justify-center mx-auto text-lamp">
                  <Film className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-cinema-100">Ready to Stream</h2>
                  <p className="text-xs text-cinema-400 mt-1.5 leading-relaxed">
                    Click below to start playing and streaming the movie to everyone in the room.
                  </p>
                </div>
                <button
                  onClick={handleHostStartBroadcast}
                  className="w-full py-3 px-6 rounded-md bg-lamp hover:bg-lamp-bright text-cinema-950 font-bold text-sm tracking-wide transition-all shadow-md flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                >
                  <Play className="w-4 h-4 fill-current" />
                  <span>Start Movie Stream</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Backdrop Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/75 backdrop-blur-sm z-40 md:hidden animate-fade-in"
            onClick={() => setIsSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Right Sidebar: Details & Controls */}
        {isSidebarOpen && (
          <aside className="fixed inset-y-0 right-0 z-50 w-full sm:w-80 max-w-[88vw] md:relative md:max-w-none md:w-80 md:z-20 flex-shrink-0 hairline-l bg-cinema-900 flex flex-col justify-between overflow-hidden shadow-2xl md:shadow-none animate-slide-left md:animate-none">
            {/* Mobile-Only Header with Close Button */}
            <div className="flex md:hidden items-center justify-between px-4 py-3 hairline-b bg-cinema-950 flex-shrink-0">
              <span className="text-xs font-bold text-cinema-100 uppercase tracking-wider flex items-center gap-2">
                {sidebarTab === 'room' ? <Users className="w-4 h-4 text-lamp" /> : <MessageSquare className="w-4 h-4 text-lamp" />}
                <span>{sidebarTab === 'room' ? `Room Details (${room.participants.length})` : 'Live Chat'}</span>
              </span>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-1.5 rounded-md text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors"
                aria-label="Close sidebar drawer"
              >
                <PanelRightClose className="w-4 h-4" />
              </button>
            </div>

            {/* Sidebar Tab Selector */}
            <div className="flex hairline-b bg-cinema-950/70 p-1 m-3 mb-0 rounded-lg border border-cinema-700/60 flex-shrink-0">
              <button
                onClick={() => setSidebarTab('room')}
                className={`flex-1 py-1.5 px-2.5 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${sidebarTab === 'room'
                    ? 'bg-cinema-800 text-cinema-100 shadow-sm'
                    : 'text-cinema-400 hover:text-cinema-200'
                  }`}
              >
                <Users className="w-3.5 h-3.5 text-lamp" />
                <span>Room ({room.participants.length})</span>
              </button>
              <button
                onClick={() => {
                  setSidebarTab('chat');
                  clearUnreadChat();
                }}
                className={`flex-1 py-1.5 px-2.5 rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${sidebarTab === 'chat'
                    ? 'bg-cinema-800 text-cinema-100 shadow-sm'
                    : 'text-cinema-400 hover:text-cinema-200'
                  }`}
              >
                <MessageSquare className="w-3.5 h-3.5 text-lamp" />
                <span>Chat</span>
                {unreadChatCount > 0 && (
                  <span className="min-w-[16px] h-4 px-1 rounded-full bg-lamp text-cinema-950 text-[10px] font-bold flex items-center justify-center">
                    {unreadChatCount}
                  </span>
                )}
              </button>
            </div>

            {/* Sidebar Content */}
            {sidebarTab === 'room' ? (
              <div className="flex-1 flex flex-col justify-between overflow-y-auto">
                <div className="p-5 space-y-6">
                  {/* Invite Link */}
                  <InviteLink roomId={room.roomId} />

                  {/* Participants */}
                  <ParticipantList
                    onTransferHost={handleTransferHost}
                    onRemoveParticipant={handleRemoveParticipant}
                  />

                  {/* Subtitles: Host Load, OpenSubtitles Search, or Sync Offset */}
                  <div className="space-y-2.5 pt-2 hairline-t">
                    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-cinema-300">
                      <span className="flex items-center gap-1.5">
                        <Subtitles className="w-3.5 h-3.5 text-lamp" />
                        <span>Subtitles</span>
                      </span>
                      {subtitleCues.length > 0 && (
                        <span className="font-mono text-cinema-400 text-[11px]">
                          Offset: {subtitleOffset === 0 ? '0.0s' : `${subtitleOffset > 0 ? '+' : ''}${subtitleOffset.toFixed(1)}s`}
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => setIsSubtitleModalOpen(true)}
                      className="w-full py-2 px-3 rounded-md bg-cinema-950 hover:bg-cinema-850 border border-cinema-700/80 text-xs text-cinema-300 hover:text-cinema-100 transition-colors flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                    >
                      <Search className="w-3.5 h-3.5 text-lamp" />
                      <span>Search OpenSubtitles</span>
                    </button>

                    {isHost && (
                      <>
                        <input
                          ref={subtitleInputRef}
                          type="file"
                          accept=".srt,.vtt"
                          className="hidden"
                          onChange={handleSubtitleFile}
                        />
                        <button
                          onClick={() => subtitleInputRef.current?.click()}
                          className="w-full py-1.5 px-3 rounded-md bg-cinema-950 hover:bg-cinema-850 border border-cinema-700/80 text-xs text-cinema-400 hover:text-cinema-100 transition-colors flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                        >
                          <Subtitles className="w-3.5 h-3.5 text-cinema-400" />
                          <span>{subtitleCues.length > 0 ? 'Replace Local File (.srt/.vtt)' : 'Load Local File (.srt/.vtt)'}</span>
                        </button>
                      </>
                    )}

                    {subtitleCues.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setSubtitleOffset(Math.round((subtitleOffset - 0.5) * 10) / 10)}
                          className="flex-1 py-1 px-2 rounded bg-cinema-950 hover:bg-cinema-800 border border-cinema-700/80 text-[11px] text-cinema-300 hover:text-cinema-100 transition-colors"
                          title="Delay subtitles by 0.5 seconds"
                        >
                          -0.5s
                        </button>
                        <button
                          onClick={() => setSubtitleOffset(0)}
                          className="px-2.5 py-1 rounded bg-cinema-950 hover:bg-cinema-800 border border-cinema-700/80 text-[11px] text-cinema-400 hover:text-cinema-100 transition-colors flex items-center gap-1"
                          title="Reset subtitle offset to 0"
                        >
                          <RotateCcw className="w-3 h-3" />
                          <span>Reset</span>
                        </button>
                        <button
                          onClick={() => setSubtitleOffset(Math.round((subtitleOffset + 0.5) * 10) / 10)}
                          className="flex-1 py-1 px-2 rounded bg-cinema-950 hover:bg-cinema-800 border border-cinema-700/80 text-[11px] text-cinema-300 hover:text-cinema-100 transition-colors"
                          title="Advance subtitles by 0.5 seconds"
                        >
                          +0.5s
                        </button>
                      </div>
                    )}

                    <p className="text-[11px] text-cinema-500">
                      {isHost
                        ? 'Search OpenSubtitles or load local files. Broadcasts to room.'
                        : 'Search OpenSubtitles or fine-tune subtitle timing to match your audio.'}
                    </p>
                  </div>

                  {/* Quick Shortcuts Helper Card */}
                  <div className="p-3.5 rounded-lg bg-cinema-950 border border-cinema-700/60 text-xs space-y-2 text-cinema-400">
                    <div className="text-[11px] font-semibold text-cinema-300 uppercase pb-1 hairline-b flex items-center gap-1.5">
                      <Keyboard className="w-3.5 h-3.5 text-lamp" />
                      <span>Quick Shortcuts</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span>Play / Pause</span>
                      <kbd className="px-1.5 py-0.5 rounded bg-cinema-800 text-cinema-200 font-mono">Space</kbd>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span>Rewind / Forward 10s</span>
                      <kbd className="px-1.5 py-0.5 rounded bg-cinema-800 text-cinema-200 font-mono">← / →</kbd>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span>Toggle Fullscreen</span>
                      <kbd className="px-1.5 py-0.5 rounded bg-cinema-800 text-cinema-200 font-mono">F</kbd>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span>Mute Audio</span>
                      <kbd className="px-1.5 py-0.5 rounded bg-cinema-800 text-cinema-200 font-mono">M</kbd>
                    </div>
                  </div>
                </div>

                {/* Bottom: Connection Details */}
                <div className="p-5 hairline-t">
                  <button
                    onClick={() => setIsTelemetryOpen(true)}
                    className="w-full py-2 px-3 rounded-md bg-cinema-950 hover:bg-cinema-800 border border-cinema-700 text-xs text-cinema-300 hover:text-cinema-100 transition-colors flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
                  >
                    <Activity className="w-3.5 h-3.5 text-lamp" />
                    <span>View Stream Quality</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-hidden flex flex-col">
                <RoomChat
                  onSendMessage={handleSendMessage}
                  onSendReaction={handleSendReaction}
                />
              </div>
            )}
          </aside>
        )}
      </div>

      {/* Stream Quality Modal */}
      <StreamTelemetryModal
        isOpen={isTelemetryOpen}
        onClose={() => setIsTelemetryOpen(false)}
        onQualityChange={handleQualityChange}
      />

      {/* Full Keyboard Shortcuts Modal */}
      {isShortcutsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-cinema-900 border border-cinema-700/80 rounded-xl max-w-sm w-full p-6 shadow-2xl text-cinema-100">
            <h3 className="text-sm font-bold text-cinema-100 mb-4 pb-2 hairline-b flex items-center gap-2">
              <Keyboard className="w-4 h-4 text-lamp" />
              <span>Keyboard Shortcuts</span>
            </h3>

            <div className="space-y-2.5 text-xs">
              <div className="flex items-center justify-between p-2 rounded bg-cinema-950 border border-cinema-700/50">
                <span className="text-cinema-300">Play / Pause</span>
                <kbd className="text-lamp bg-cinema-800 px-2 py-0.5 rounded font-mono">Space</kbd>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-cinema-950 border border-cinema-700/50">
                <span className="text-cinema-300">Rewind 10 seconds</span>
                <kbd className="text-lamp bg-cinema-800 px-2 py-0.5 rounded font-mono">← Left Arrow</kbd>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-cinema-950 border border-cinema-700/50">
                <span className="text-cinema-300">Forward 10 seconds</span>
                <kbd className="text-lamp bg-cinema-800 px-2 py-0.5 rounded font-mono">→ Right Arrow</kbd>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-cinema-950 border border-cinema-700/50">
                <span className="text-cinema-300">Toggle Fullscreen</span>
                <kbd className="text-lamp bg-cinema-800 px-2 py-0.5 rounded font-mono">F</kbd>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-cinema-950 border border-cinema-700/50">
                <span className="text-cinema-300">Mute Audio</span>
                <kbd className="text-lamp bg-cinema-800 px-2 py-0.5 rounded font-mono">M</kbd>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-cinema-950 border border-cinema-700/50">
                <span className="text-cinema-300">Toggle Subtitles</span>
                <kbd className="text-lamp bg-cinema-800 px-2 py-0.5 rounded font-mono">S</kbd>
              </div>
            </div>

            <button
              onClick={() => setIsShortcutsOpen(false)}
              className="mt-6 w-full py-2 rounded-md bg-cinema-800 hover:bg-cinema-750 text-xs font-semibold text-cinema-200 hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* OpenSubtitles Search Modal */}
      <SubtitleSearchModal
        isOpen={isSubtitleModalOpen}
        onClose={() => setIsSubtitleModalOpen(false)}
      />

      {/* Host Change Media Modal */}
      <ChangeMediaModal
        isOpen={isChangeMediaOpen}
        onClose={() => setIsChangeMediaOpen(false)}
        onSwitchToYouTube={handleSwitchToYouTube}
        onSwitchToLocalFile={handleSwitchToLocalFile}
      />
    </div>
  );
}
